// src/store/reducers/slices/vendorSlice.ts

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {BASE_URL} from "../api";
import { AppDispatch, RootState } from "../../../store/store";
import { Car } from "../types/car";

interface VendorDashboard {
  total_cars: number;
  approved: number;
  pending: number;
}

interface VendorState {
  dashboard: VendorDashboard | null;
  cars: Car[];
  loading: boolean;
  error: any;
}

const initialState: VendorState = {
  dashboard: null,
  cars: [],
  loading: false,
  error: null,
};

async function api<T>(
  path: string,
  token: string,
  options?: RequestInit
): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    ...options,
  });

  const data = await res.json();
  if (!res.ok) throw data;
  return data;
}


const vendorSlice = createSlice({
  name: "vendor",
  initialState,
  reducers: {
    start(state) {
      state.loading = true;
      state.error = null;
    },

    dashboardSuccess(state, action: PayloadAction<VendorDashboard>) {
      state.loading = false;
      state.dashboard = action.payload;
    },

    carsSuccess(state, action: PayloadAction<Car[]>) {
      state.loading = false;
      state.cars = action.payload;
    },

    addCarSuccess(state, action: PayloadAction<Car>) {
      state.loading = false;
      state.cars.unshift(action.payload);
    },

    fail(state, action: PayloadAction<any>) {
      state.loading = false;
      state.error = action.payload;
    },

    clear(state) {
      state.dashboard = null;
      state.cars = [];
      state.loading = false;
      state.error = null;
    },
  },
});

export const {
  start,
  dashboardSuccess,
  carsSuccess,
  addCarSuccess,
  fail,
  clear,
} = vendorSlice.actions;

export default vendorSlice.reducer;

export const fetchVendorDashboard =
  () =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(start());

    try {
      const token = getState().auth.access_token;
      if (!token) throw new Error("Unauthorized");

      const data = await api<VendorDashboard>(
        "/vendor/dashboard",
        token
      );

      dispatch(dashboardSuccess(data));
    } catch (err: any) {
      dispatch(fail(err));
    }
  };


  export const fetchVendorCars =
  () =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(start());

    try {
      const token = getState().auth.access_token;
      if (!token) throw new Error("Unauthorized");

      const cars = await api<Car[]>(
        "/vendor/cars",
        token
      );

      dispatch(carsSuccess(cars));
    } catch (err: any) {
      dispatch(fail(err));
    }
  };

  export const addVendorCar =
  (payload: Omit<Car, "id">) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(start());

    try {
      const token = getState().auth.access_token;
      if (!token) throw new Error("Unauthorized");

      const res = await api<{ car: Car }>(
        "/vendor/cars",
        token,
        {
          method: "POST",
          body: JSON.stringify(payload),
        }
      );

      dispatch(addCarSuccess(res.car));
    } catch (err: any) {
      dispatch(fail(err));
    }
  };

